import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Page } from '../../layout/AppLayout.jsx'
import Card from '../../components/ui/Card.jsx'
import Badge from '../../components/ui/Badge.jsx'
import Button from '../../components/ui/Button.jsx'
import Icon from '../../components/ui/Icon.jsx'
import api from '../../lib/api.js'
import { formatCurrency, formatDate } from '../../lib/format.js'
import { useCompany } from '../../context/CompanyContext.jsx'
import { invoiceStatus, isOutgoing } from '../invoices/invoiceStatus.js'

/*
 * Dashboard — new design (README D): 4 KPI cards (CA, en attente, en retard,
 * payées) + recent invoices. Real endpoints (confirmed against staging data):
 *   GET /stats/invoices -> { stats: { sent:{...}, received:{...} } }
 *   GET /invoices/      -> { invoices:[...], pagination:{...} }   (trailing slash!)
 * "CA / en attente / payées" come from the `sent` (outgoing) aggregates; overdue
 * is computed from the outgoing invoices flagged is_overdue.
 */
export default function DashboardPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { currentCompanyId } = useCompany()
  const [sent, setSent] = useState(null)
  const [overdue, setOverdue] = useState(null)
  const [recent, setRecent] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Wait until the active company is known: firing company-scoped requests
    // with no X-Company-Id returns empty data and causes a flash of "—".
    if (!currentCompanyId) return undefined
    let active = true
    setLoading(true)
    Promise.allSettled([
      api.get('/stats/invoices'),
      api.get('/invoices/', { params: { per_page: 100, page: 1 } }),
    ]).then(([s, inv]) => {
      if (!active) return
      if (s.status === 'fulfilled') setSent(s.value.data?.stats?.sent || null)
      if (inv.status === 'fulfilled') {
        const list = inv.value.data?.invoices || []
        setRecent(list.slice(0, 6))
        const od = list
          .filter((i) => i.sender_company_id === currentCompanyId && i.is_overdue)
          .reduce((sum, i) => sum + (Number(i.amount_including_tax) || 0), 0)
        setOverdue(od)
      }
    }).finally(() => active && setLoading(false))
    return () => { active = false }
  }, [currentCompanyId])

  const year = sent?.current_year ?? new Date().getFullYear()
  const kpis = [
    { label: t('dashboard.kpi.revenue'), icon: 'invoice', tone: 'var(--color-primary)', value: sent ? formatCurrency(sent.total_excl_vat_current_year ?? sent.total_excl_vat) : '—', badge: String(year), sub: t('dashboard.kpi.revenueSub', { year }) },
    { label: t('dashboard.kpi.pending'), icon: 'send', tone: 'var(--color-info)', value: sent ? formatCurrency(sent.total_unpaid) : '—' },
    { label: t('dashboard.kpi.overdue'), icon: 'alert-triangle', tone: 'var(--color-warning)', value: overdue != null ? formatCurrency(overdue) : '—' },
    { label: t('dashboard.kpi.paid'), icon: 'check', tone: 'var(--color-success)', value: sent ? formatCurrency(sent.total_paid) : '—' },
  ]

  const action = (
    <Button variant="primary" icon="plus" onClick={() => navigate('/app/invoices/new')}>{t('invoices.new')}</Button>
  )

  return (
    <Page title={t('dashboard.title')} action={action}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 18, marginBottom: 22 }}>
        {kpis.map((k) => (
          <Card key={k.label}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--color-text)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{k.label}</span>
                {k.badge && <span style={{ fontSize: 11, fontWeight: 700, color: k.tone, background: 'var(--color-bg-lilac-strong)', borderRadius: 999, padding: '2px 8px' }}>{k.badge}</span>}
              </span>
              <span style={{ color: k.tone, display: 'inline-flex' }}><Icon name={k.icon} size={19} /></span>
            </div>
            <div style={{ fontFamily: 'var(--font-title)', fontSize: 24, fontWeight: 800, color: 'var(--color-ink)' }}>
              {loading ? '…' : k.value}
            </div>
            {k.sub && <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 4 }}>{k.sub}</div>}
          </Card>
        ))}
      </div>

      <Card padding={0}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px', borderBottom: '1px solid var(--color-border-row)' }}>
          <h2 className="h-title" style={{ fontSize: 16 }}>{t('dashboard.recentInvoices')}</h2>
          <Button variant="secondary" icon="invoice" onClick={() => navigate('/app/invoices')}>{t('dashboard.viewAll')}</Button>
        </div>
        {loading ? (
          <div style={{ padding: 28, color: 'var(--color-text-muted)' }}>{t('common.loading')}</div>
        ) : recent.length === 0 ? (
          <div style={{ padding: '40px 28px', textAlign: 'center', color: 'var(--color-text-muted)' }}>{t('dashboard.empty')}</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--color-bg-lilac)' }}>
                {[t('invoices.columns.number'), t('invoices.columns.type'), t('invoices.columns.dueDate'), t('invoices.columns.amount'), t('invoices.columns.status')].map((h, i) => (
                  <th key={h} style={{ textAlign: i === 3 ? 'right' : 'left', padding: '11px 20px', fontSize: 11.5, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recent.map((inv) => {
                const st = invoiceStatus(inv)
                const out = isOutgoing(inv)
                return (
                  <tr key={inv.id} style={{ borderTop: '1px solid var(--color-border-row)', cursor: 'pointer' }}
                    onClick={() => navigate(`/app/invoices/${inv.id}`)}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-bg-lilac)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = '')}>
                    <td style={{ padding: '13px 20px', fontWeight: 600, color: 'var(--color-ink)' }}>{inv.invoice_number || `#${inv.id}`}</td>
                    <td style={{ padding: '13px 20px' }}>
                      <Badge tone={out ? 'info' : 'neutral'}>{out ? t('invoices.direction.sent') : t('invoices.direction.received')}</Badge>
                    </td>
                    <td style={{ padding: '13px 20px', color: 'var(--color-text)' }}>{formatDate(inv.issue_date)}</td>
                    <td style={{ padding: '13px 20px', textAlign: 'right', fontFamily: 'var(--font-title)', fontWeight: 600 }}>{formatCurrency(inv.amount_including_tax, inv.currency || 'EUR')}</td>
                    <td style={{ padding: '13px 20px' }}><Badge tone={st.tone}>{t(st.key)}</Badge></td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </Card>
    </Page>
  )
}
